"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "@/i18n/navigation";
import { useHeaderContextTitleValue } from "@/contexts/HeaderTitleContext";

// Largeur du fondu appliqué au bord droit quand le titre ne tient pas.
const FADE_WIDTH = 32;
const FADE = `linear-gradient(to right, #000 calc(100% - ${FADE_WIDTH}px), transparent 100%)`;

/**
 * Titre de contexte de la barre du haut : le nom de la formation en cours de
 * lecture, à droite de la recherche.
 *
 * Un titre long ne se coupe pas net et ne pose pas de points de suspension : il
 * s'efface en fondu sur son bord droit, et seulement quand il déborde vraiment
 * — un titre court reste net jusqu'à sa dernière lettre. Sous 1024 px, la
 * barre a déjà la recherche et les compteurs : le titre disparaît.
 */
export default function HeaderContextTitle() {
    const contextTitle = useHeaderContextTitleValue();
    const textRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);

    const title = contextTitle?.title || null;

    useEffect(() => {
        const element = textRef.current;
        if (!element) return undefined;

        const measure = () =>
            setOverflowing(element.scrollWidth > element.clientWidth + 1);

        measure();

        // La largeur disponible change avec la fenêtre, et le texte change de
        // longueur quand la police d'interface finit de charger.
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        if (typeof document !== "undefined" && document.fonts?.ready) {
            document.fonts.ready.then(measure).catch(() => {});
        }

        return () => observer.disconnect();
    }, [title]);

    if (!title) return null;

    const mask = overflowing
        ? { maskImage: FADE, WebkitMaskImage: FADE }
        : undefined;

    // Le texte déborde volontairement de son cadre : c'est le cadre qui coupe,
    // et c'est ce débordement que la mesure ci-dessus compare.
    const textClass =
        "block whitespace-nowrap text-sm text-gray-500 dark:text-grayDark-400 transition-colors";

    return (
        <div className="hidden lg:flex items-center gap-3 min-w-0 flex-1">
            <span className="w-px h-5 bg-gray-200 dark:bg-borderDark shrink-0" />
            {/* Aucune largeur imposée : le titre prend ce que la barre lui
                laisse, s'affiche en entier dès qu'il y tient, et ne cède que
                lorsque la place manque vraiment. */}
            <div
                ref={textRef}
                style={mask}
                className="min-w-0 overflow-hidden"
            >
                {contextTitle.href ? (
                    <Link
                        href={contextTitle.href}
                        title={title}
                        className={`${textClass} hover:text-black dark:hover:text-white`}
                    >
                        {title}
                    </Link>
                ) : (
                    <span title={title} className={textClass}>
                        {title}
                    </span>
                )}
            </div>
        </div>
    );
}
